import { Injectable } from '@angular/core';
import { HttpMethodType } from '@app/app.constant';
import { HttpCallService } from '@core/services/http-call.service';
import {
  License,
  ILicenseHttpResponse,
  ILicenseSaveHttp,
  EvideLicenseKeyInfo
} from '@app/models/license/license.model';
import { ILicenseSsoInfo, ILicenseSsoSaveRequest, IUserWorkLoginInfo } from '@app/models/license/license-sso.model';

import { map, Observable } from 'rxjs';

const HttpServiceMethods = {
  License: 'EvideLicense', // Admin - get byid
  GetLicenseInfo: 'EvideLicense/GetLicenseInfo',
  GetMyLicenses: 'EvideLicense/GetMyLicenses', // Admin
  GetAllLicenses: 'EvideLicense/GetAllLicenses',
  SaveLicense: 'EvideLicense/SaveLicense',
  // Licence logo for the top bar - System Admin only
  UploadLogo: 'EvideLicense/UploadLogo',
  RemoveLogo: 'EvideLicense/RemoveLogo',
  GetUserLicenses: 'User/GetLicenses',
  // Organisation sign-in (SSO) - System Admin only
  GetLicenseSso: 'EvideLicenseSso/GetByLicense',
  SaveLicenseSso: 'EvideLicenseSso/Save',
  GetUserWorkLogin: 'EvideLicenseSso/WorkLogin',
  UnlinkUserWorkLogin: 'EvideLicenseSso/Unlink'
};

@Injectable({
  providedIn: 'root'
})
export class LicenseService {
  constructor(private httpService: HttpCallService) { }

  /** Stores a new logo for the licence (PNG, JPEG or SVG, up to 500 KB) and returns the licence with its URL. */
  public uploadLogo(licenseId: number, file: File): Observable<License> {
    const form = new FormData();
    form.append('file', file, file.name);
    return this.httpService
      .callService(HttpMethodType.POST, `${HttpServiceMethods.UploadLogo}/${licenseId}`, form)
      .pipe(map((res: ILicenseHttpResponse) => License.adapt(res)));
  }

  /** Removes the licence's logo; the top bar goes back to Evide's. */
  public removeLogo(licenseId: number): Observable<License> {
    return this.httpService
      .callService(HttpMethodType.DELETE, `${HttpServiceMethods.RemoveLogo}/${licenseId}`)
      .pipe(map((res: ILicenseHttpResponse) => License.adapt(res)));
  }

  public list(): Observable<License[]> {
    return this.httpService
      .callService(HttpMethodType.GET, HttpServiceMethods.License)
      .pipe(
        map((res) =>
          res.map((item: ILicenseHttpResponse) => License.adapt(item))
        )
      );
  }

  public create(license: License) {
    return this.httpService.callService(
      HttpMethodType.POST,
      HttpServiceMethods.License,
      license
    );
  }

  public update(license: License) {
    return this.httpService.callService(
      HttpMethodType.PUT,
      HttpServiceMethods.License,
      license
    );
  }

  public delete(id: number) {
    return this.httpService.callService(
      HttpMethodType.DELETE,
      HttpServiceMethods.License + `?id=${id}`
    );
  }

  public get(id: number): Observable<License> {
    return this.httpService
      .callService(
        HttpMethodType.GET,
        HttpServiceMethods.License + `/${id}`
      )
      .pipe(map((item: ILicenseHttpResponse) => License.adapt(item)));
  }

  public getMyLicenses(): Observable<License[]> {
    return this.httpService
      .callService(HttpMethodType.GET, HttpServiceMethods.GetMyLicenses)
      .pipe(
        map((res) =>
          res.map((item: ILicenseHttpResponse) => License.adapt(item))
        )
      );
  }

  public getAllLicenses(): Observable<License[]> {
    return this.httpService
      .callService(HttpMethodType.GET, HttpServiceMethods.GetAllLicenses)
      .pipe(
        map((res) =>
          res.map((item: ILicenseHttpResponse) => License.adapt(item))
        )
      );
  }

  public saveLicense(license: ILicenseSaveHttp) {
    return this.httpService.callService(
      HttpMethodType.POST,
      HttpServiceMethods.SaveLicense,
      license
    );
  }

  public getUserLicenses(): Observable<EvideLicenseKeyInfo[]> {
    return this.httpService
      .callService(HttpMethodType.GET, HttpServiceMethods.GetUserLicenses)
      .pipe(map((res) => res as EvideLicenseKeyInfo[]));
  }

  /** A licence's organisation sign-in (SSO) settings; empty, switched-off settings when it has none. */
  public getLicenseSso(licenseId: number): Observable<ILicenseSsoInfo> {
    return this.httpService
      .callService(HttpMethodType.GET, `${HttpServiceMethods.GetLicenseSso}/${licenseId}`)
      .pipe(map((res) => res as ILicenseSsoInfo));
  }

  /** Whether a user's account has a work login linked (System Admin only; read live from B2C). */
  public getUserWorkLogin(userGuid: string): Observable<IUserWorkLoginInfo> {
    return this.httpService
      .callService(HttpMethodType.GET, `${HttpServiceMethods.GetUserWorkLogin}/${userGuid}`)
      .pipe(map((res) => res as IUserWorkLoginInfo));
  }

  /** Removes the user's work login and ends their sessions (System Admin only). */
  public unlinkUserWorkLogin(userGuid: string): Observable<IUserWorkLoginInfo> {
    return this.httpService
      .callService(HttpMethodType.POST, `${HttpServiceMethods.UnlinkUserWorkLogin}/${userGuid}`)
      .pipe(map((res) => res as IUserWorkLoginInfo));
  }

  /** Creates or updates a licence's SSO settings. A 400 carries the message for the screen. */
  public saveLicenseSso(request: ILicenseSsoSaveRequest): Observable<ILicenseSsoInfo> {
    return this.httpService
      .callService(HttpMethodType.POST, HttpServiceMethods.SaveLicenseSso, request)
      .pipe(map((res) => res as ILicenseSsoInfo));
  }
}
